import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAccounts,
  getAccount,
  assignOperator,
  clearOperatorAssignment,
  rechargeAccount,
  zeroAccount,
  banAccount,
  listBindings,
  addBinding,
  switchBinding,
  endBinding,
  listTransactions,
  reportConsume,
  createAccount,
  fetchOperators,
  getAccountStats,
  updateAccount,
  listAccountApps,
  getDailySpend,
  batchCreateAccounts,
  batchBanAccounts,
  batchZeroAccounts,
} from './accounts';
import type { UpdateAccountDto, BatchAccountStatusResult } from './accounts';
import { accountKeys, operatorKeys, transactionKeys } from './query-keys';
import type {
  AccountQuery,
  AssignOperatorReq,
  RechargeReq,
  AddBindingReq,
  ReportConsumeReq,
  TransactionQuery,
  CreateAccountReq,
  SwitchBindingReq,
} from './types';

export function useAccountList(query: AccountQuery) {
  return useQuery({
    queryKey: accountKeys.list(query),
    queryFn: () => listAccounts(query),
  });
}

export function useAccountDetail(accountId: string | null) {
  return useQuery({
    queryKey: accountId ? accountKeys.detail(accountId) : accountKeys.all,
    queryFn: () => getAccount(accountId as string),
    enabled: !!accountId,
  });
}

export function useAccountBindings(accountId: string | null) {
  return useQuery({
    queryKey: accountId ? accountKeys.bindings(accountId) : accountKeys.all,
    queryFn: () => listBindings(accountId as string),
    enabled: !!accountId,
  });
}

export function useAccountTransactions(
  accountId: string | null,
  query: TransactionQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: accountId ? accountKeys.transactions(accountId, query) : accountKeys.all,
    queryFn: () => listTransactions(accountId as string, query),
    enabled: !!accountId && (options?.enabled ?? true),
  });
}

export function useAssignOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, ...req }: { accountId: string } & AssignOperatorReq) =>
      assignOperator(accountId, req),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

export function useClearOperatorAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) => clearOperatorAssignment(accountId),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

export function useRechargeAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, ...req }: { accountId: string } & RechargeReq) =>
      rechargeAccount(accountId, req),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
      void qc.invalidateQueries({ queryKey: accountKeys.stats() });
    },
  });
}

export function useZeroAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) => zeroAccount(accountId),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
      void qc.invalidateQueries({ queryKey: accountKeys.stats() });
    },
  });
}

export function useBanAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) => banAccount(accountId),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
      void qc.invalidateQueries({ queryKey: accountKeys.stats() });
    },
  });
}

export function useAddBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, ...req }: { accountId: string } & AddBindingReq) =>
      addBinding(accountId, req),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.bindings(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

export function useEndBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, bindingId }: { accountId: string; bindingId: number }) =>
      endBinding(accountId, bindingId),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.bindings(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

export function useReportConsume() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, ...req }: { accountId: string } & ReportConsumeReq) =>
      reportConsume(accountId, req),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.stats() });
      void qc.invalidateQueries({ queryKey: transactionKeys.lists() });
    },
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateAccountReq) => createAccount(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
      void qc.invalidateQueries({ queryKey: accountKeys.stats() });
    },
  });
}

export function useOperatorOptions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: operatorKeys.list({}),
    queryFn: fetchOperators,
    staleTime: 5 * 60_000,
    throwOnError: false,
    enabled: options?.enabled ?? true,
  });
}

export function useAccountStatsQuery() {
  return useQuery({
    queryKey: accountKeys.stats(),
    queryFn: getAccountStats,
  });
}

export function useSwitchBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, ...req }: { accountId: string } & SwitchBindingReq) =>
      switchBinding(accountId, req),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.bindings(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

export function useUpdateAccountMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, ...dto }: { accountId: string } & UpdateAccountDto) =>
      updateAccount(accountId, dto),
    onSuccess: (_data, { accountId }) => {
      void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
      void qc.invalidateQueries({ queryKey: accountKeys.stats() });
    },
  });
}

export function useAccountApps(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountKeys.apps(),
    queryFn: listAccountApps,
    enabled: options?.enabled ?? true,
  });
}

export function useDailySpend(bizDate: string) {
  return useQuery({
    queryKey: accountKeys.dailySpend(bizDate),
    queryFn: () => getDailySpend(bizDate),
    enabled: Boolean(bizDate),
  });
}

export function useBatchCreateAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: batchCreateAccounts,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountKeys.lists() });
      void qc.invalidateQueries({ queryKey: accountKeys.stats() });
    },
  });
}

function invalidateAfterBatch(
  qc: ReturnType<typeof useQueryClient>,
  result: BatchAccountStatusResult,
) {
  result.succeeded.forEach((accountId) => {
    void qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
  });
  void qc.invalidateQueries({ queryKey: accountKeys.lists() });
  void qc.invalidateQueries({ queryKey: accountKeys.stats() });
}

export function useBatchBanAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: batchBanAccounts,
    onSuccess: (result) => invalidateAfterBatch(qc, result),
  });
}

export function useBatchZeroAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: batchZeroAccounts,
    onSuccess: (result) => invalidateAfterBatch(qc, result),
  });
}
